import React, { useState, memo } from 'react';
import classnames from 'classnames';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// Types
import { CaledndarIntegration2023SlideType } from './types';
import { OnboardingFlowType } from 'Dashboard/components/Onboarding/OnboardingWizard/types';

// Constants
import {
  CaledndarIntegration2023Slides,
  CaledndarIntegration2023SlidesInvited,
} from './CalendarIntegration2023Slides.constants';

// Hooks
import { useOnboardingState } from 'Shared/utils/hooks/use-onboarding-state';

// Components
import { WpIllustration, Illustrations } from 'Wpds/components/illustration';
import { WpPageLayout, WpPageBody } from 'Wpds/components/page-layout';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Styles
import './CalendarIntegration2023IntroSlides.scss';

const Slide = memo(
  ({ slide, index, currentStep }: { slide: CaledndarIntegration2023SlideType; index: number; currentStep: number }) => {
    const number = index + 1;
    const isActive = number === currentStep;

    const [isEntered, setIsEntered] = useState(false);
    const baseClassName = 'CalendarIntegration2023IntroSlides';

    const classes = classnames(
      `${baseClassName}__content__list__steps-wrapper__steps__step`,
      `${baseClassName}__content__list__steps-wrapper__steps__step--${number}`,
      isActive ? `${baseClassName}__content__list__steps-wrapper__steps__step--active` : '',
      isEntered ? `${baseClassName}__step-animation--entered` : ''
    );
    return (
      <CSSTransition
        in={true}
        appear={true}
        timeout={1000}
        classNames={`${baseClassName}__step-animation`}
        unmountOnExit
        onEntered={() => setIsEntered(true)}
      >
        <div className={classes}>
          <div className={`${baseClassName}__content__list__steps-wrapper__steps__step__number`}>{number}.</div>
          <h5 className={`${baseClassName}__content__list__steps-wrapper__steps__step__title`}>{slide.title}</h5>
        </div>
      </CSSTransition>
    );
  }
);

const CalendarIntegration2023IntroSlides = () => {
  const { flowType, handleAdvanceOnboardingState } = useOnboardingState();

  const [currentStep, setCurrentStep] = useState(1);
  const [animationDirection, setAnimationDirection] = useState<'in' | 'back'>('in');

  const slides =
    flowType === OnboardingFlowType.calendarIntegration2023
      ? CaledndarIntegration2023Slides
      : CaledndarIntegration2023SlidesInvited;

  const currentSlide = slides[currentStep - 1];

  const handleIncreaseStep = () => {
    if (currentStep === 3) {
      handleAdvanceOnboardingState();
    } else {
      setAnimationDirection('in');
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 1);
    }
  };

  const handleBackClick = () => {
    setAnimationDirection('back');
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
    }, 1);
  };

  const handleSkipClick = () => {
    handleAdvanceOnboardingState({ didSkip: true });
  };

  const baseClassName = 'CalendarIntegration2023IntroSlides';

  const NavigationButtons = (
    <WpButtonGroup stacked>
      <WpButton onClick={handleIncreaseStep} size="lg" block>
        Next
      </WpButton>
      <WpButton onClick={handleSkipClick} size="lg" variant="tertiary" block>
        Skip
      </WpButton>
    </WpButtonGroup>
  );

  return (
    <WpPageLayout width="full" className={baseClassName}>
      <WpPageBody>
        <div className={`${baseClassName}__top-bar`}>
          <div className={`${baseClassName}__top-bar__back-btn`}>
            {currentStep !== 1 && (
              <WpButton variant="tertiary" onClick={handleBackClick}>
                <WpIcon name="long-arrow-left" />
                Back
              </WpButton>
            )}
          </div>
          <div className={`${baseClassName}__top-bar__logo`}>
            <WpIllustration
              src={Illustrations.pi_logo_full_red_with_text}
              className={`${baseClassName}__top-bar__logo__img`}
              alt="Predictive Index logo"
            />
          </div>
        </div>
        <div className={`${baseClassName}__content`}>
          {/* {currentStep !== 1 && (
            <div className={`${baseClassName}__back-btn`}>
              <WpButton variant="tertiary" onClick={handleBackClick}>
                <WpIcon name="long-arrow-left" />
                Back
              </WpButton>
            </div>
          )} */}

          <div className={`${baseClassName}__content__list`}>
            <WpIllustration
              src={Illustrations.pi_logo_circle_red}
              className={`${baseClassName}__content__list__logo`}
              alt="Predictive Index logo"
            />
            <h1 className={`${baseClassName}__content__list__title`}>Getting started</h1>
            <p className={`${baseClassName}__content__list__desc`}>To get set up for success, do these 3 things:</p>
            <div className={`${baseClassName}__content__list__steps-wrapper`}>
              <div className={`${baseClassName}__content__list__steps-wrapper__steps`}>
                {slides.map((slide, index) => (
                  <Slide
                    slide={slide}
                    index={index}
                    key={`calendar-integration-slide-${index}`}
                    currentStep={currentStep}
                  />
                ))}
              </div>
              <CSSTransition
                in={true}
                appear={true}
                timeout={1000}
                classNames={`${baseClassName}__step-btns-animation`}
                unmountOnExit
              >
                <div className={`${baseClassName}__step-btns-animation`}>{NavigationButtons}</div>
              </CSSTransition>
            </div>
          </div>
          <div className={`${baseClassName}__content__step`} style={{ backgroundColor: currentSlide.backgroundColor }}>
            <SwitchTransition mode="out-in">
              <CSSTransition
                key={currentStep}
                classNames={`fade-${animationDirection}`}
                appear
                addEndListener={(node, done) => {
                  node.addEventListener('transitionend', done, false);
                }}
              >
                <div className={`${baseClassName}__content__step__container`}>
                  <h1 className={`${baseClassName}__content__step__container__title`}>{currentSlide.heading}</h1>
                  <h4 className={`${baseClassName}__content__step__container__desc`}>{currentSlide.description}</h4>
                  <div>
                    <img src={currentSlide.image} className={`${baseClassName}__content__step__container__img`} />
                  </div>
                </div>
              </CSSTransition>
            </SwitchTransition>
            <div className={`${baseClassName}__content__step__dots`}>
              {slides.map((slide, index) => {
                const active = index + 1 === currentStep;
                return (
                  <div
                    className={`${baseClassName}__content__step__dots__dot ${
                      active ? `${baseClassName}__content__step__dots__dot--active` : ''
                    }`}
                    key={`slide-dot-${index}`}
                  />
                );
              })}
            </div>
          </div>
          <div className={`${baseClassName}__mobile-nav-btns`}>{NavigationButtons}</div>
        </div>
      </WpPageBody>
    </WpPageLayout>
  );
};

export default CalendarIntegration2023IntroSlides;
